/**
 * Resolution of the live that follows the next one ("N+2").
 *
 * A pure function, on purpose: the dashboard has no React test harness, and
 * `useInterpolatedTiming.test.ts` tests a hand-copied replica of the hook's
 * `compute()` that has already drifted from the real one. Keeping this logic
 * out of the hook means its tests exercise the code that actually runs.
 */

import type { TimingData } from '@timer-manager/shared';

/** A playlist is one day: an N+2 further out than this is corrupt data. */
export const FOLLOWING_MAX_HORIZON_MS = 24 * 3_600 * 1_000;
const DAY_MS = 24 * 3_600 * 1_000;

/**
 * Three states, deliberately unrepresentable otherwise:
 *  - `unknown` — nothing proven: the collector has not finished (or could not
 *    finish) walking the playlist, runs older code, or sent a live we cannot
 *    place in time. Never displayed as an assertion.
 *  - `none`    — the collector walked the whole playlist past the next live
 *    and found nothing. The ONLY state that allows "no other live" on screen.
 *  - `live`    — a named, placeable N+2. `at` is always a valid Date: that is
 *    what keeps `formatClock` (Intl.DateTimeFormat, which throws RangeError on
 *    an Invalid Date — a white screen in mid-render) safe by construction.
 */
export type FollowingLive =
  | { kind: 'unknown' }
  | { kind: 'none' }
  | { kind: 'live'; displayName: string; at: Date };

/**
 * @param data           the anchor's TimingData
 * @param nextLiveEndMs  start of the next live (N+1) as the dashboard shows it:
 *                       already delay-adjusted and snapped to the second — the
 *                       stabilised value from useInterpolatedTiming, NEVER
 *                       `nextLiveAt`, which turns into a running clock once
 *                       the countdown clamps at 0.
 * @param delayMs        transmission + processing delay, the same amount
 *                       already subtracted from `nextLiveEndMs`.
 */
export function resolveFollowingLive(
  data: TimingData,
  nextLiveEndMs: number | null,
  delayMs: number,
): FollowingLive {
  // Positive marker only. Absence of the fields means "unknown", never
  // "none" — a collector on older code, a cold cache after a failover, or a
  // walk that failed all emit nothing at all.
  if (data.following_live_known !== true) return { kind: 'unknown' };

  const name = data.following_live_display_name;
  if (typeof name !== 'string' || name.trim().length === 0) return { kind: 'none' };

  // Both ways of dating N+2 are relative to N+1 one way or another.
  if (nextLiveEndMs === null) return { kind: 'unknown' };

  // 1. Absolute clocked anchor (ISO UTC). On a playlist with clocked items
  //    this is what the OTAV countdown actually targets. Same delay basis as
  //    `nextLiveEndMs`.
  let atMs: number | null = null;
  const iso = data.following_live_start_time;
  if (typeof iso === 'string') {
    const parsed = Date.parse(iso);
    if (Number.isFinite(parsed)) {
      atMs = parsed - delayMs;
      // Only the TIME OF DAY of `nextLiveEndMs` is NTP-true: its calendar day
      // is the browser's (parseServerTimeOfDay). On a device whose date is off
      // by whole days, bring the absolute ISO onto that same day — otherwise
      // a clocked N+2 would never be placeable there. A no-op on a device with
      // the right date, where the ISO is placeable as is.
      if (!isPlaceable(atMs, nextLiveEndMs)) {
        atMs += Math.round((nextLiveEndMs - atMs) / DAY_MS) * DAY_MS;
      }
    }
  }

  // 2. Offset from N+1, for a live that merely chains after it. Gated on the
  //    FAILURE of the absolute path — unparseable OR implausible — not on the
  //    field merely being present: a malformed ISO must not throw away a
  //    perfectly usable offset and make the card vanish.
  if (atMs === null || !isPlaceable(atMs, nextLiveEndMs)) {
    const off = data.following_live_offset_seconds;
    atMs = typeof off === 'number' && Number.isFinite(off) ? nextLiveEndMs + off * 1000 : null;
  }

  // Named but unplaceable degrades to `unknown`: better to say nothing than
  // to show a name with a wrong time — or to claim the playlist is over.
  if (atMs === null || !isPlaceable(atMs, nextLiveEndMs)) return { kind: 'unknown' };

  // Snapped to the second, like `nextLiveEndMs` itself: for a chained live
  // the card then reads exactly N+1's displayed time + the offset.
  const at = new Date(Math.round(atMs / 1000) * 1000);
  // Validate the Date ITSELF, not just the number: a finite millisecond count
  // can still fall outside Date's range (±8.64e15 ms) and yield an Invalid
  // Date — e.g. from an absurd `remaining_time_until_next_live` in a corrupt
  // message. Caught by the fuzz test in following.test.ts: `Number.isFinite`
  // alone let 16 Invalid Dates through.
  if (!Number.isFinite(at.getTime())) return { kind: 'unknown' };
  return { kind: 'live', displayName: name, at };
}

/** N+2 must start strictly after N+1, and within the same day. */
function isPlaceable(atMs: number, nextLiveEndMs: number): boolean {
  return (
    Number.isFinite(atMs) &&
    atMs > nextLiveEndMs &&
    atMs - nextLiveEndMs <= FOLLOWING_MAX_HORIZON_MS
  );
}
